// The verdict strip, as data: every branch that decides what it says lives here, and nothing here
// touches the DOM or the store. That is what makes it testable — the strip has twelve states and,
// while it was a render function, not one of them had a test.

import { outOfTen, pointsDelta, pointsLong, pointsShort } from "./points.ts";
import { verdictGlyph, verdictLabel } from "./moveRating.ts";
import type { PracticeMove, PracticeResult, PracticeStatus } from "../state/keys.ts";

export type VerdictActionId = "signUp" | "takeBack" | "finishOffBook" | "toggleDetails";

export interface VerdictAction {
    id: VerdictActionId;
    label: string;
    variant: "btn-primary" | "btn-ghost" | "btn-link";
}

export interface VerdictView {
    /** Drives the colour through [data-verdict]; "idle" until a move is rated. */
    kind: string;
    tone: string;
    badge: string;
    move: string;
    insight: string;
    /** On a small screen the line's idea waits here for the first move. */
    idea: string;
    actions: VerdictAction[];
    /** The verdicts that get the stamp animation, and what makes it play again. */
    stamped: boolean;
    stampKey: string;
}

export interface VerdictInput {
    status: PracticeStatus;
    line: { idea?: string; color: string } | null;
    move: PracticeMove | null;
    result: PracticeResult | null;
    offbook: { rarity: "rare" | "unknown" | "paused" | "repertoire" } | null;
    notice: string | null;
    engineLoading: boolean;
    detailsOpen: boolean;
    userToMove: boolean;
}

const STAMPED = new Set(["best", "good", "inaccuracy", "mistake", "blunder"]);

const OFFBOOK_REASON: Record<string, string> = {
    rare: "Few play it: the book ends here.",
    unknown: "Nobody plays it: the book ends here.",
    repertoire: "Not your repertoire's move: take it back, or finish here.",
};

export function finishedText(result: PracticeResult | null): string {
    if (!result) return "";
    if (result.isRetry) return "That was a retry: it is for learning and does not change your numbers.";
    if (!result.counted) return "Too short to count as a practice. Play at least 3 moves.";
    return result.accuracy === null
        ? `${result.ownMoves} moves played.`
        : `${result.ownMoves} moves played with ${Math.round(result.accuracy)}% accuracy.`;
}

export function verdictView(input: VerdictInput): VerdictView {
    const base = (over: Partial<VerdictView> = {}): VerdictView => ({
        kind: "idle", tone: input.move?.insight?.tone || "neutral",
        badge: "", move: "", insight: "", idea: "", actions: [], stamped: false, stampKey: "",
        ...over,
    });

    if (input.status === "locked") {
        return base({
            kind: "locked", badge: "Free practices used",
            insight: "Create your free account to keep practicing. Everything you earned comes with you.",
            actions: [{ id: "signUp", label: "Create free account", variant: "btn-primary" }],
        });
    }
    if (input.status === "unavailable") {
        return base({
            badge: "Free board",
            insight: "The line catalog could not be loaded. Reload the page to try again.",
        });
    }
    if (input.status === "idle" || !input.line) {
        return base({ badge: "Loading", insight: "Setting up a line for you." });
    }
    if (input.status === "finished") {
        return base({
            kind: "finished",
            badge: input.result?.title || "Practice finished",
            insight: finishedText(input.result),
        });
    }
    if (!input.move) {
        return base({
            badge: input.userToMove ? "Your move" : "Your sparring partner opens",
            insight: "Play what you would play in a real game. Every move gets a verdict.",
            idea: input.line.idea || "",
        });
    }

    const verdict = input.move.verdict;
    const view = base({ move: input.move.label });

    if (verdict.status === "pending") {
        view.kind = "pending";
        view.badge = input.engineLoading ? "Warming up the engine" : "Rating your move";
    } else if (verdict.status === "unavailable") {
        view.kind = "unavailable";
        view.badge = "No verdict";
        view.insight = input.move.insight?.text || "The engine could not rate this move on this device.";
    } else {
        view.kind = verdict.code || "idle";
        view.badge = `${verdictGlyph(verdict.code ?? null)} ${verdictLabel(verdict.code ?? null)}`;
        view.insight = input.move.insight?.text || "";
    }

    if (input.status === "offbook") {
        const why = input.offbook?.rarity === "paused"
            ? (input.notice || "")
            : (OFFBOOK_REASON[input.offbook?.rarity ?? ""] || "");
        view.insight = `${view.insight} ${why}`.trim();
        view.actions.push({ id: "takeBack", label: "Take back", variant: "btn-primary" });
        view.actions.push({ id: "finishOffBook", label: "Finish here", variant: "btn-ghost" });
    } else if (verdict.status === "rated" && (verdict.code === "mistake" || verdict.code === "blunder"
        || outOfRepertoire(input.move.stats?.repertoire as RepertoireStats | null | undefined))) {
        view.actions.push({ id: "takeBack", label: "Take back", variant: "btn-ghost" });
    }
    if (verdict.status === "rated") {
        view.actions.push({
            id: "toggleDetails", label: input.detailsOpen ? "Hide details" : "Details", variant: "btn-link",
        });
    }

    view.stamped = STAMPED.has(view.kind);
    // The animation replays when the verdict or the move changes, and not on every other render.
    view.stampKey = `${view.kind}:${view.move}`;
    return view;
}

/** good is green, bad is red, even is the grey of a number that says nothing either way. */
export type ChipTone = "good" | "bad" | "even" | "muted";

/** One piece of a row: a number or two words, coloured by what it means for the user. */
export interface Chip {
    text: string;
    tone?: ChipTone;
    /** The number the row is about, set bolder than the rest. */
    strong?: boolean;
    /** A word before it, in grey, that stays on its line ("best Nf6"). */
    lead?: string;
    /** A second number after it, on the same line, with its own colour ("5/10 −0.8"). */
    tail?: { text: string; tone: ChipTone };
}

/**
 * A row of the breakdown, as marks and numbers rather than a sentence: it is read at a glance between two
 * moves, and a sentence per row was four sentences. `text` is the sentence anyway, for the tooltip and
 * for a screen reader.
 */
export interface DetailRow {
    label: string;
    /** ✓, ✗, ★ or –, with its colour: the answer of the row before any number. */
    mark: { glyph: string; tone: ChipTone } | null;
    chips: Chip[];
    text: string;
    muted?: boolean;
}

/** What the user's repertoire said about the position the move was played in. */
export interface RepertoireStats {
    /** There is an active repertoire to check against at all. */
    hasRepertoire: boolean;
    /** That repertoire reaches this position. */
    covers: boolean;
    /** The move played is one the repertoire plays here. */
    inRepertoire: boolean;
    /** What the repertoire plays here, in SAN. Empty when the line ends at this position. */
    expectedSan: string[];
}

/** "Nf3", "Nf3 or Bg7", "Nf3, Bg7 or c4": a repertoire rarely offers more than a handful. */
function joinMoves(sans: string[]): string {
    const shown = sans.slice(0, 3);
    if (shown.length < 2) return shown[0] || "";
    return `${shown.slice(0, -1).join(", ")} or ${shown[shown.length - 1]}`;
}

/** The repertoire's take on the move, in one sentence. */
export function repertoireText(rep: RepertoireStats, san: string): string {
    if (!rep.covers) return "Your repertoire does not cover this line.";
    if (rep.inRepertoire) {
        const others = joinMoves(rep.expectedSan.filter((move) => move !== san));
        return others ? `Following your repertoire. It also plays ${others}.` : "Following your repertoire.";
    }
    const expected = joinMoves(rep.expectedSan);
    return expected
        ? `Out of repertoire. Your file plays ${expected}.`
        : "Your repertoire ends here, so this move is your own.";
}

/** Whether the move left a repertoire that had an answer here: what lets it be taken back before the book ends. */
export function outOfRepertoire(rep: RepertoireStats | null | undefined): boolean {
    return !!rep && rep.hasRepertoire && rep.covers && !rep.inRepertoire && rep.expectedSan.length > 0;
}

function repertoireRow(rep: RepertoireStats, san: string): DetailRow {
    const text = repertoireText(rep, san);
    if (!rep.covers) return { label: "Repertoire", mark: { glyph: "–", tone: "muted" }, chips: [{ text: "Not covered", tone: "muted" }], text };
    if (rep.inRepertoire) {
        const others = rep.expectedSan.filter((move) => move !== san).slice(0, 3);
        return {
            label: "Repertoire", mark: { glyph: "✓", tone: "good" }, text,
            chips: [{ text: "In repertoire", tone: "good" }, ...(others.length ? [{ text: `also ${others.join(", ")}`, tone: "muted" as const }] : [])],
        };
    }
    if (!rep.expectedSan.length) {
        return { label: "Repertoire", mark: { glyph: "–", tone: "even" }, chips: [{ text: "Past its end", tone: "even" }], text };
    }
    return {
        label: "Repertoire", mark: { glyph: "✗", tone: "bad" }, text,
        chips: [{ lead: "yours", text: rep.expectedSan.slice(0, 3).join(", "), tone: "bad", strong: true }],
    };
}

/** Scores past this are a mate, as `scoreToCp` writes one. */
const MATE_CP = 1000;

/** A score in pawns as a player reads it: "+2.1", "−0.4", "0.0", "#3" for a mate, "#−3" for one against. */
export function pawns(cp: number): string {
    if (Math.abs(cp) > MATE_CP) {
        const moves = 21 - Math.round(Math.abs(cp) / 100);
        return `#${cp < 0 ? "−" : ""}${moves}`;
    }
    const value = (Math.abs(cp) / 100).toFixed(1);
    if (value === "0.0") return "0.0";
    return `${cp < 0 ? "−" : "+"}${value}`;
}

const SOUND = new Set(["best", "good"]);

/**
 * The engine's row: the move it would have played, what the one played cost in pawns, and the score
 * before and after it. Scores are from White's side like every other score on the site; the cost is
 * the mover's, so it is never above zero and is red when it matters.
 */
function engineRow(move: PracticeMove): DetailRow {
    const v = move.verdict;
    const loss = v.loss ?? 0;
    const text = v.bestUci === move.uci
        ? "This is the engine's choice."
        : `Best was ${v.bestSan}. Winning chances given away: ${loss < 0.05 ? "none" : `${loss.toFixed(1)} points`}.`;
    const white = move.fenBefore?.split(" ")[1] !== "b";
    const fromWhite = (cp: number) => (white ? cp : -cp);
    const has = typeof v.bestCp === "number" && typeof v.playedCp === "number";
    const sound = SOUND.has(v.code ?? "");
    // A good move that costs a little is grey, not green: what it gave away is still not a gain
    const tone: ChipTone = sound ? "even" : "bad";

    if (v.bestUci === move.uci) {
        return {
            label: "Engine", mark: { glyph: "★", tone: "good" }, text,
            chips: [{ text: "Engine's move", tone: "good" }, ...(has ? [{ text: pawns(fromWhite(v.playedCp!)), tone: "muted" as const }] : [])],
        };
    }
    const chips: Chip[] = [];
    if (has) {
        const mate = Math.abs(v.bestCp!) > MATE_CP || Math.abs(v.playedCp!) > MATE_CP;
        const drop = v.playedCp! - v.bestCp!;
        if (!mate) chips.push({ text: drop > -5 ? "0.0" : pawns(drop), tone: drop > -5 ? "even" : tone, strong: true });
        chips.push({ text: `${pawns(fromWhite(v.bestCp!))} → ${pawns(fromWhite(v.playedCp!))}`, tone: mate ? tone : "muted" });
    }
    chips.push({ lead: "best", text: v.bestSan || "", strong: true });
    // "Best move" up top and a tick here read as two opinions: a move as good as the best gets the star too
    const glyph = v.code === "best" ? "★" : sound ? "✓" : "✗";
    return { label: "Engine", mark: { glyph, tone: sound ? "good" : "bad" }, chips, text };
}

/** Half a point out of ten either way is a number that says something; less is the usual noise. */
const CLEAR_DELTA_PCT = 3;

/**
 * How a move went for whoever played it, the one way the site says it in little room: the score out of
 * ten, coloured against the usual, with the difference beside it ("5/10 −0.8").
 */
export function scoreChips(scorePct: number, usualPct: number): Chip[] {
    const diff = scorePct - usualPct;
    const tone: ChipTone = diff >= CLEAR_DELTA_PCT ? "good" : diff <= -CLEAR_DELTA_PCT ? "bad" : "even";
    const delta = pointsDelta(scorePct, usualPct);
    return [{ text: pointsShort(scorePct), tone, strong: true, ...(delta === "0" ? {} : { tail: { text: delta, tone } }) }];
}

/** The breakdown under the strip: what the repertoire, the engine, the database and the user's own games say. */
export function verdictDetails(move: PracticeMove | null, isLoggedIn: boolean): DetailRow[] {
    if (!move || move.verdict.status !== "rated") return [];
    const rows: DetailRow[] = [];

    // The repertoire goes first: it is the question the study asks, and the engine is the second opinion
    const rep = move.stats?.repertoire as RepertoireStats | null | undefined;
    if (rep?.hasRepertoire) {
        rows.push(repertoireRow(rep, move.san));
    } else if (!isLoggedIn) {
        const text = "Log in and upload your repertoire to see whether this move follows it.";
        rows.push({ label: "Repertoire", mark: null, chips: [{ text: "Log in to check it", tone: "muted" }], text, muted: true });
    } else if (rep) {
        const text = "No active repertoire. Upload a PGN and every move is checked against it.";
        rows.push({ label: "Repertoire", mark: null, chips: [{ text: "None uploaded", tone: "muted" }], text, muted: true });
    }

    rows.push(engineRow(move));

    const db = move.stats?.db as
        { n: number; sharePct: number; scorePct: number; baselinePct: number; positionN: number; topSan: string } | undefined;
    if (db && db.n > 0) {
        rows.push({
            label: "Players", mark: null,
            text: `${Math.round(db.sharePct)}% play it (${formatCount(db.n)} games), taking ${pointsLong(db.scorePct)}. `
                + `From this position players take ${outOfTen(db.baselinePct)} on average.`,
            chips: [
                { text: `${Math.round(db.sharePct)}%`, strong: true },
                { text: `${formatCount(db.n)} games`, tone: "muted" },
                ...scoreChips(db.scorePct, db.baselinePct),
            ],
        });
    } else if (db) {
        rows.push({
            label: "Players", mark: null,
            text: `Not played in ${formatCount(db.positionN)} games. The usual move is ${db.topSan}.`,
            chips: [{ text: "0%", tone: "bad", strong: true }, { lead: "usual", text: db.topSan, strong: true }],
        });
    }

    const mine = move.stats?.mine as { n: number; positionN: number; scorePct: number } | undefined;
    if (mine && mine.n > 0) {
        rows.push({
            label: "Your games", mark: null,
            text: `You played it in ${mine.n} of ${mine.positionN} games, taking ${pointsLong(mine.scorePct)}.`,
            chips: [{ text: `${mine.n}/${mine.positionN} games`, tone: "muted" }, ...scoreChips(mine.scorePct, 50)],
        });
    } else if (mine) {
        rows.push({
            label: "Your games", mark: null,
            text: `You reached this position ${mine.positionN} times and never played it.`,
            chips: [{ text: "Never played", tone: "muted" }, { text: `${mine.positionN} games here`, tone: "muted" }],
        });
    } else if (!isLoggedIn) {
        const text = "Log in and import your games to see how this move works for you.";
        rows.push({ label: "Your games", mark: null, chips: [{ text: "Log in to see them", tone: "muted" }], text, muted: true });
    }
    return rows;
}

export function formatCount(n: number): string {
    if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `${Math.round(n / 1e3)}K`;
    return String(n);
}
